import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('thaman_token'));
    const [isLoading, setIsLoading] = useState(true);

    const isAuthenticated = !!user;
    const isAdmin = user?.role === 'admin';

    const login = useCallback((newToken, newUser) => {
        localStorage.setItem('thaman_token', newToken);
        localStorage.setItem('thaman_user', JSON.stringify(newUser));
        setToken(newToken);
        setUser(newUser);
    }, []);

    const logout = useCallback(async () => {
        try {
            if (token) {
                await fetch(`${API_BASE_URL}/auth/logout`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                });
            }
        } catch (err) {
            // Ignore logout API errors
        }
        localStorage.removeItem('thaman_token');
        localStorage.removeItem('thaman_user');
        setToken(null);
        setUser(null);
    }, [token]);

    // Verify token on load
    useEffect(() => {
        const verifyToken = async () => {
            if (!token) {
                setIsLoading(false);
                return;
            }

            try {
                const response = await fetch(`${API_BASE_URL}/auth/me`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                });

                if (response.ok) {
                    const userData = await response.json();
                    setUser(userData);
                } else {
                    // Token invalid — clear it
                    localStorage.removeItem('thaman_token');
                    localStorage.removeItem('thaman_user');
                    setToken(null);
                    setUser(null);
                }
            } catch (err) {
                // Try to use cached user data if API is down
                const cached = localStorage.getItem('thaman_user');
                if (cached) {
                    try { setUser(JSON.parse(cached)); } catch (_) {}
                }
            } finally {
                setIsLoading(false);
            }
        };
        verifyToken();
    }, [token]);

    const value = {
        user,
        token,
        isAuthenticated,
        isAdmin,
        isLoading,
        login,
        logout,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}
